import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

/**
 * Fonte única de verdade sobre o estado do app.
 *
 * Existem DOIS estados diferentes, que precisam ser tratados separadamente:
 *
 * - foreground: o app foi suspenso pelo sistema operacional (Cordova pause/resume).
 *   No celular o WebView é congelado de verdade: os timers param sozinhos e, ao voltar,
 *   os ticks acumulados disparam de uma vez.
 *
 * - visible: a aba/janela está visível (Page Visibility API).
 *   No desktop o navegador NÃO congela nada, apenas aplica throttling. Pausar aqui é
 *   uma escolha de cada tela: Orders, CallAttendant e o painel de senhas precisam
 *   continuar rodando, pois o lojista deixa a aba em segundo plano para ouvir o alerta.
 */
@Injectable()
export class AppStateService {
  private foregroundSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private visibleSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  private lastResumeTime: number = Date.now();
  private initialized: boolean;

  constructor(private platform: Platform) {}

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.platform.pause.subscribe(() => {
      this.foregroundSubject.next(false);
    });

    this.platform.resume.subscribe(() => {
      this.lastResumeTime = Date.now();
      this.foregroundSubject.next(true);
    });

    this.visibleSubject.next(!document.hidden);
    document.addEventListener('visibilitychange', () => {
      const visible: boolean = !document.hidden;
      if (visible) {
        this.lastResumeTime = Date.now();
      }
      this.visibleSubject.next(visible);
    });
  }

  isForeground(): boolean {
    return this.foregroundSubject.value;
  }

  isVisible(): boolean {
    return this.visibleSubject.value;
  }

  /**
   * Emite apenas em MUDANÇA de estado. O iOS dispara resume e visibilitychange com poucos
   * milissegundos de diferença, e o distinctUntilChanged garante uma emissão só.
   */
  get foreground$(): Observable<boolean> {
    return this.foregroundSubject.asObservable().pipe(distinctUntilChanged());
  }

  get visible$(): Observable<boolean> {
    return this.visibleSubject.asObservable().pipe(distinctUntilChanged());
  }

  /**
   * Momento do último retorno do background.
   */
  getLastResumeTime(): number {
    return this.lastResumeTime;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/app-state.service.ts